// The branches that change the checklist in the Record's words (spec C8, S17): Non-renewal and
// Policy inactive, their steps, the tag, the dialogs and the toasts. Pure functions, so the screens
// and the tests read the same lines.
import type {
  BranchReason,
  BranchStepKey,
  BranchView,
  RenewalBranch,
  RenewalDetail,
  RenewalRow,
} from '../auth/api';

export const branchNames: Record<RenewalBranch, string> = {
  non_renewal: 'Non-renewal',
  inactive: 'Policy inactive',
};

/** The reasons in the Non-renewal dialog, in this order (spec C8). */
export const branchReasonLabels: Record<BranchReason, string> = {
  carrier_termination: 'Carrier termination or other',
  non_compliance: 'Non-compliance with recommendations',
  non_compliance_second: 'Second non-renewal, same non-compliance',
};

/** One line under the reasons: what changes with the one picked. */
export const branchReasonHelp: Record<BranchReason, string> = {
  carrier_termination: 'The insured gets the non-renewal notice, with the AE in CC.',
  non_compliance:
    'The insured gets the notice and the compliance contact an email, both with the AE in CC. The AE starts the requote at 20 days if still not compliant.',
  non_compliance_second:
    'Not re-shopped: the AE advises the insured in writing and by phone that the policy will lapse.',
};

const longDate = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});
/** "Dec 20, 2026", the way dates are typed (spec C7). */
export const typedDate = (ymd: string) => longDate.format(new Date(`${ymd}T00:00:00Z`));

const shortDate = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});

/** The header and list tag: "Non-renewal eff Dec 20" (yellow) or "Policy inactive" (grey). */
export function branchTag(b: NonNullable<RenewalRow['branch']>): {
  text: string;
  className: string;
  tip: string;
} {
  const day = shortDate.format(new Date(`${b.effectiveDate}T00:00:00Z`));
  return b.kind === 'non_renewal'
    ? {
        text: `Non-renewal eff ${day}`,
        // The kit's status and tag color class for Non-renewal's yellow.
        className: 'tag sc-yellow',
        tip: `The carrier is not renewing, effective ${typedDate(b.effectiveDate)}. Status stays Pending until the AE accepts the handoff.`,
      }
    : {
        text: 'Policy inactive',
        className: 'tag t-grey',
        tip: `Inactive on the carrier site, cancelled effective ${typedDate(b.effectiveDate)}. Closes as Cancelled at its last step.`,
      };
}

export const branchStepTitles: Record<BranchStepKey, string> = {
  nr_notice: 'Upload notice',
  nr_email: 'Email insured',
  nr_activity: 'Log activity',
  nr_hand: 'Hand to AE',
  pi_notice: 'Upload notice',
  pi_email: 'Email the AE',
  pi_activity: 'Log activity',
  pi_close: 'Close as Cancelled',
};

/** "Why this step" under each title (spec C8; the wording moves to Settings with the others). */
export function branchStepWhy(key: BranchStepKey, b: BranchView): string {
  switch (key) {
    case 'nr_notice':
      return "The carrier's non-renewal notice goes on the record and to the insured.";
    case 'nr_email':
      return b.emails.includes('non_compliance')
        ? 'The insured gets the non-renewal notice and the compliance contact an email, the AE in CC on both.'
        : 'The insured gets the non-renewal notice, with the AE in CC.';
    case 'nr_activity':
      return 'Log the notice in AMS360 so the account shows it.';
    case 'nr_hand':
      return 'The AE takes it from here. Status becomes Non-renewal when the AE accepts.';
    case 'pi_notice':
      return 'The cancellation notice or endorsement, if the carrier has one.';
    case 'pi_email':
      return 'The AE is told the policy was cancelled and when.';
    case 'pi_activity':
      return 'Log it in AMS360 with the email to the AE attached.';
    case 'pi_close':
      return 'The renewal closes as Cancelled with the sheet comment.';
  }
}

/** The line at the top of the branch's steps. */
export function branchLine(b: BranchView): string {
  const when = typedDate(b.effectiveDate);
  if (b.branch === 'inactive') return `Policy inactive steps. Cancelled effective ${when}.`;
  const why = b.reason ? `${branchReasonLabels[b.reason]}. ` : '';
  return `Non-renewal steps. ${why}Effective ${when}.`;
}

/** The note Hand to AE opens with on step 4 (spec C8). */
export const handNote = (b: BranchView) =>
  b.reason === 'non_compliance_second' ? 'Second non-renewal, same issue' : '';

/** The stamp of Hand to AE: who it went to and its state. */
export function handLine(b: BranchView): string | null {
  if (!b.hand) return null;
  const to = b.hand.to ?? 'the AE';
  switch (b.hand.state) {
    case 'waiting':
      return `Handed to ${to}. Waiting for the answer.`;
    case 'accepted':
      return `With ${to}. Status: Non-renewal.`;
    default:
      return `Handed to ${to}.`;
  }
}

/** The toasts. */
export const branchSetText = (branch: RenewalBranch) =>
  `Switched to the ${branchNames[branch].toLowerCase()} steps.`;
export const branchBackText = 'Back to the renewal steps.';
export const branchUndoneText = 'Undone.';
export const branchTickText = (key: BranchStepKey) => `${branchStepTitles[key]} done.`;
export const branchUntickText = (key: BranchStepKey) => `${branchStepTitles[key]} unticked.`;

/** Why a branch action did not go through, from the server's answer. */
export function branchRefusal(error: string | undefined): string {
  switch (error) {
    case 'on_branch':
      return 'This renewal is already on a branch. Read it again.';
    case 'not_on_branch':
      return 'This renewal is back on the renewal steps. Read it again.';
    case 'handoff_open':
      return 'The handoff to the AE is still open. Wait for it to come back.';
    case 'renewal_closed':
      return 'This renewal is closed now. Read it again.';
    case 'undo_expired':
      return 'Could not undo. The renewal changed since, or 10 minutes passed.';
    case 'reason_required':
      return 'Say why.';
    case 'bad_date':
      return 'Enter a date like Dec 20, 2026.';
    case 'notice_required':
      return 'Upload the notice first.';
    case 'no_email_sent':
      return 'Mark the email as sent first.';
    case 'not_last':
    case 'undo_not_allowed':
      return 'Only the last step can be unticked, by who ticked it the same day or the Manager.';
    case 'step_locked':
    case 'step_not_open':
      return 'The steps changed since they were read. Read them again.';
    default:
      return 'Could not save. Try again.';
  }
}

/** The renewal steps menu items while on a branch, and why the switch is not offered. */
export function branchMenu(
  r: Pick<RenewalDetail, 'branch' | 'handoff'>,
  stepsClosed: boolean,
): { enter: boolean; back: boolean; why: string | null } {
  if (r.branch) {
    return {
      enter: false,
      back: !r.handoff,
      why: r.handoff ? 'The handoff to the AE is still open.' : null,
    };
  }
  if (r.handoff) return { enter: false, back: false, why: 'A handoff to an AE is open.' };
  if (stepsClosed) return { enter: false, back: false, why: 'Step 6 is done.' };
  return { enter: true, back: false, why: null };
}
